import { type FC } from 'react';
import {
  Loader2Icon,
  CheckCircle2Icon,
  XCircleIcon,
  TargetIcon,
} from 'lucide-react';
import { Card } from '../ui/card';
import { cn } from '@/lib/utils';

interface GoalToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

function parseArgs(args: any, argsText: string): Record<string, any> {
  try {
    return typeof args === 'object' && args ? args : JSON.parse(argsText);
  } catch {
    return {};
  }
}

function parseResult(result?: string): {
  count: number;
  total: number;
  error?: string;
} {
  if (result === undefined) {
    return { count: 0, total: 0 };
  }
  try {
    const parsed = typeof result === 'string' ? JSON.parse(result) : result;
    return {
      count: Array.isArray(parsed?.goals) ? parsed.goals.length : 0,
      total: typeof parsed?.total === 'number' ? parsed.total : 0,
      error: parsed?.error || parsed?.message,
    };
  } catch {
    return { count: 0, total: 0 };
  }
}

/**
 * The list_goals tool call card.
 *
 * Reports only that goals were looked up and how many were found — the goals
 * themselves belong in the assistant's prose, not duplicated in the tool card.
 */
const GoalToolCallComponent: FC<GoalToolCallProps> = ({ args, argsText, result, status }) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = status?.type === 'complete' && !isError;

  const parsedArgs = parseArgs(args, argsText);
  const { count, total, error } = parseResult(result);
  const hasError = isError || (result !== undefined && count === 0 && !!error);

  const metric = typeof parsedArgs.metric === 'string' ? parsedArgs.metric.trim() : '';

  const runningLabel = metric
    ? `Checking goals for ${metric}...`
    : 'Checking goals...';
  // Past tense once the call is done — it describes what happened, not a heading.
  const contextLabel = metric
    ? `Looked up goals for ${metric}`
    : 'Looked up goals';

  const resultLabel = total === 0
    ? (metric ? `No goals set for ${metric}.` : 'No goals have been set.')
    : `Found ${total} ${total === 1 ? 'goal' : 'goals'}.`;

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning ? 'bg-primary/10 border-b border-primary/20' : hasError ? 'bg-destructive/10' : 'bg-muted/50',
      )}>
        {isRunning ? (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">{runningLabel}</p>
          </div>
        ) : hasError ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <TargetIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">{contextLabel}</p>
            </div>
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm m-0 text-destructive">
                {error || 'Could not load goals'}
              </p>
            </div>
          </div>
        ) : isComplete ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <TargetIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">{contextLabel}</p>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">{resultLabel}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">{runningLabel}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export const GoalToolCall = GoalToolCallComponent as React.ComponentType<any>;
